import React from "react";
import Image from "next/image";
import styles from "./LoginContent.module.css";
import { useRouter } from "next/router";
import {
  APPLICATION,
  INVALID_LOGIN,
  OTP,
  USER_LOCKED_OUT,
} from "@/utils/Auth/authConstants";
import { LOGIN_SUCCESS } from "@/utils/Auth/authConstants";
import { processLoginData } from "../../../utils/parentMapping";
import { userLogin } from "@/utils/Auth/loginForgeRock";
import { useState, useEffect } from "react";
import {
  getCurrentPage,
  setForgeRockAuthInfo,
  setPostAcademicInfo,
  setSchoolLogo,
} from "@/redux/dataSlice";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { CHANGE_PASSWORD } from "@/utils/constants";
import { userLogout } from "@/utils/Auth/userManager";
import eyeshow from "@/assets/images2/eyeshow.png";
import eyehide from "@/assets/images2/eyehide.png";
import { removeStorage } from "@/utils/storage";
import SEO from "@/components/Seo";
import OverlayLoader from "@/parent-components/OverlayLoader";
import Link from "next/link";
import { datadogLogs } from "@datadog/browser-logs";

const LoginContent = (data) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const [username, setUsername] = useState(null);
  const [password, setPassword] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [intentMessage, setIntentMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [otpStatus, setOtpStatus] = useState("FIRSTOTP");
  const [responseInput, setResponseInput] = useState(null);
  const [otpType, setOtpType] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const processedData = processLoginData(data);


  const { messageIntent, errorIntent } = router.query;
  const currenPage = useSelector(getCurrentPage);

  useEffect(() => {
    try{
    userLogout(dispatch, router);
    removeStorage("currentStep");
    dispatch(setPostAcademicInfo(null));
    dispatch(setSchoolLogo(null));
    setIntentMessage(messageIntent);
    setErrorMessage(errorIntent);
    }catch(e){
      console.log('error: ', e);
    }
  }, []);

  useEffect(() => {
    if (username || password) {
      setErrorMessage(null);
    }
  }, [username, password]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    const processOtpStatus = async () => {
      console.log('otpStatus: ', otpStatus);
      try {
        if (otpStatus === INVALID_LOGIN) {
          setErrorMessage(INVALID_LOGIN);
          setIsLoading(false);
        } else if (otpType === LOGIN_SUCCESS) {

          if (currenPage === APPLICATION) {
            router.push("/parent/application");
          } else {
            router.push("/parent/dashboard");
          }

        } else if (otpStatus === OTP) {
          dispatch(
            setForgeRockAuthInfo({
              userEmail: username,
              userPassword: password,
              getToken: null,
              responsePayload: responseInput,
              givenName: null,
              familyName: null,
              educationId: null,
            })
          );

          router.push("/parent/verify-otp-login");
        } else {
          setIsLoading(false);
          userLogout(dispatch, router);
        }
        
      } catch (e) {
        console.error("Error processing OTP status:", e);
        setOtpStatus(null);
        setIsLoading(false);

        datadogLogs.logger.error("LoginContent error", {
          errorMessage: e.message,
          stack: e.stack,
          component: "LoginContent",
          function: "processOtpStatus",
        });

      }
    };
    processOtpStatus();
  }, [otpStatus]);

  const handleForgotPasswordClick = () => {
    router.push("/parent/forgot-password");
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Simple email regex
    return email.length <= 40 && emailRegex.test(email);
  };

  const isPasswordValid = (password) => {
    return password.length >= 8;
  };

  const handleLoginClick = async () => {
    try {
      setErrorMessage(null);
      setIntentMessage(null);
      setIsLoading(true);

      if (!username || !password) {
        setErrorMessage("All fields are required.");
        setIsLoading(false);
        return;
      }

      if (!isValidEmail(username)) {
        setErrorMessage("Invalid Email");
        setIsLoading(false);
        return;
      }

      if (!isPasswordValid(password)) {
        setErrorMessage("Invalid Password");
        setIsLoading(false);
        return;
      }

      const result = await userLogin(username, password);


      if (result?.status === INVALID_LOGIN) {
        setErrorMessage(INVALID_LOGIN);
        setIsLoading(false);
      }else if (result?.status === USER_LOCKED_OUT) {
        setErrorMessage(USER_LOCKED_OUT);
        setIsLoading(false);
      }
      setResponseInput(result?.response);
      setOtpStatus(result?.status);
      setOtpType(result?.type);
      setIsLoading(false);
    } catch (e) {
      console.log("Login failed:", e);
      setErrorMessage("Something went wrong. Please try again.");
      setOtpStatus(null);
      setIsLoading(false);

      datadogLogs.logger.error("LoginContent error", {
        errorMessage: e.message,
        stack: e.stack,
        component: "LoginContent",
        function: "handleLoginClick",
      });

    }

  };

  if (isLoading) {
    return <OverlayLoader />;
  }

  return (
    <>
      <SEO title={processedData?.formTitle} />
      <div
        className={styles.container}
        style={{ background: `url(${processedData?.backgroundimage})` }}
        data-testid="login-container"
      >
        <div className={`${styles.logo_container}`}>
          <Link href={processedData?.logoLink || "#"}>
            <Image
              width={151}
              height={40}
              alt="logo"
              src={processedData?.logo}
              className={styles.logo}
            />
          </Link>
        </div>

        <div className={styles.card} data-testid="login-card">

          <div className={styles.card_info}>
            <h1 data-testid="login-title">{processedData?.formTitle ?? "Login to your account"}</h1>
            <p
              className={styles.second_title}
              dangerouslySetInnerHTML={{ __html: processedData?.formDesc }}
            />

            <h3>{processedData.formFields?.[0]?.label ?? "Email*"}</h3>
            <div className={styles.input_container}>
              <input
                data-testid="input-username"
                type="text"
                placeholder={
                  processedData?.formFields?.[0]?.placeholder ?? "Email"
                }
                className={styles.input_box}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleLoginClick();
                  }
                }}
              />
            </div>

            <h3>{processedData?.formFields?.[1]?.label ?? "Password*"}</h3>
            <div className={styles.input_container}>
              <input
                data-testid="input-password"
                type={showPassword ? "text" : "password"}
                placeholder={
                  processedData?.formFields?.[1]?.placeholder ?? "Password"
                }
                className={`${styles.input_box} ${styles.password_input}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleLoginClick();
                  }
                }}
              />

              <div
                data-testid="toggle-password-visibility"
                className={styles.icon_container}
                onClick={togglePasswordVisibility}
              >
                <Image
                  width={16}
                  height={16}
                  alt="eye"
                  src={showPassword ? eyehide : eyeshow}
                />
              </div>
            </div>
            {errorMessage && (
              <p data-testid="error-message" className={styles.error_message}>{errorMessage}</p>
            )}
            {intentMessage && (
              <p className={styles.intent_message}>{intentMessage}</p>
            )}

            <div className={styles.auth_options}>
              <label className={styles.input_label}>
                <input
                  type="checkbox"
                  name="remember"
                  className={styles.custom_checkbox}
                />
                <span className={styles.input_label}>
                  {processedData?.formFields?.[3]?.label ?? "Remember Me"}
                </span>
              </label>
              <a
                data-testid="forgot-password"
                style={{ cursor: "pointer" }}
                className={styles.input_label}
                onClick={handleForgotPasswordClick}
              >
                {processedData?.formFields?.[4]?.label ?? "Forgot Password?"}
              </a>
            </div>

            <button
              data-testid="login-button"
              className={styles.signup_btn}
              onClick={handleLoginClick}
              disabled={isLoading}
            >
              {isLoading ? (
                <div className={styles.loader}></div> // Show loader
              ) : (
                <h3>{processedData?.formButton?.name ?? "Login"}</h3>
              )}
            </button>
          </div>
        </div>

        <div className={styles.sub_container} data-testid="form-footer">
          <p
            className={styles.info_h3}
            dangerouslySetInnerHTML={{ __html: processedData?.formfooter }}
          />
        </div>
      </div>
    </>
  );
};

export default LoginContent;
